import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function ProductsList() {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const navigate = useNavigate();

  const load = () => {
    setError(null);
    Promise.all([
      axios.get('/api/admin/products'),
      axios.get('/api/admin/categories'),
    ]).then(([p, c]) => {
      setProducts(p.data);
      setCategories(c.data);
    }).catch(err => {
      const status = err.response?.status;
      const body = err.response?.data;
      setError(
        `טעינה נכשלה (${status || 'no response'}): ${typeof body === 'object' ? JSON.stringify(body) : body || err.message}`
      );
      setProducts([]);
    });
  };

  useEffect(() => { load(); }, []);

  if (products === null && !error) return <div className="loading">טוען...</div>;
  if (error) {
    return (
      <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#b91c1c' }}>
        {error}
      </div>
    );
  }

  const visible = filterCategory
    ? products.filter(p => String(p.category_id) === filterCategory)
    : products;

  return (
    <>
      <div className="breadcrumb">
        <span>מוצרים</span>
      </div>

      <div className="section-header">
        <h3>קטלוג מוצרים</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '0.85rem', fontFamily: 'inherit' }}
          >
            <option value="">כל הקטגוריות</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/categories')}>ניהול קטגוריות</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ מוצר חדש</button>
        </div>
      </div>

      <div className="product-grid">
        {visible.map(p => (
          <div key={p.id} className="product-card" onClick={() => navigate(`/products/${p.id}`)}>
            <div className="product-card-image">
              {p.image_url ? <img src={p.image_url} alt="" /> : <div className="product-card-image-placeholder">ללא תמונה</div>}
            </div>
            <div className="product-card-body">
              <div className="product-card-name">{p.name}</div>
              <div className="product-card-meta">
                {p.category_name && <span className="badge-light">{p.category_name}</span>}
                {p.model_number && <span className="product-card-model">{p.model_number}</span>}
              </div>
              {p.manufacturer && <div className="product-card-manufacturer">{p.manufacturer}</div>}
              <div className="product-card-footer">
                <span>{p.gates_count} שערים</span>
                {p.default_cycle_limit ? <span>{p.default_cycle_limit.toLocaleString()} סייקלים</span> : null}
                {p.speed_mps ? <span>{p.speed_mps} מ״ש</span> : null}
              </div>
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <p style={{ color: '#9ca3af' }}>{filterCategory ? 'אין מוצרים בקטגוריה הזו.' : 'אין מוצרים עדיין. צור את הראשון.'}</p>
        )}
      </div>

      {showNew && (
        <NewProductModal
          categories={categories}
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); navigate(`/products/${id}`); }}
        />
      )}
    </>
  );
}

function NewProductModal({ categories, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', category_id: '', model_number: '', manufacturer: '',
    default_cycle_limit: '', speed_mps: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const res = await axios.post('/api/admin/products', {
        ...form,
        category_id: form.category_id || null,
        default_cycle_limit: form.default_cycle_limit ? parseInt(form.default_cycle_limit) : null,
        speed_mps: form.speed_mps === '' ? null : form.speed_mps,
      });
      onCreated(res.data.id);
    } catch (err) {
      setError(err.response?.data?.error || 'יצירה נכשלה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>מוצר חדש</h2>

        <div className="form-group">
          <label>שם המוצר</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="למשל: DD DECOTHERM" />
        </div>

        <div className="form-group">
          <label>קטגוריה</label>
          <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
            <option value="">ללא קטגוריה</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>מספר דגם</label>
          <input value={form.model_number} onChange={e => setForm({ ...form, model_number: e.target.value })} placeholder="S4SW-001X16EU" />
        </div>

        <div className="form-group">
          <label>יצרן</label>
          <input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} placeholder="יצרן" />
        </div>

        <div className="form-group">
          <label>ברירת מחדל - סייקלים לטיפול</label>
          <input type="number" value={form.default_cycle_limit} onChange={e => setForm({ ...form, default_cycle_limit: e.target.value })} placeholder="10000" />
        </div>

        <div className="form-group">
          <label title="מהירות תנועה לפי מפרט היצרן. משמש לחישוב אוטומטי של זמן הפתיחה/סגירה הצפוי לכל שער מהמודל הזה, יחד עם גובה הפתח שהטכנאי מזין בשטח.">
            מהירות (מטרים לשנייה)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.speed_mps}
            onChange={e => setForm({ ...form, speed_mps: e.target.value })}
            placeholder="לדוגמה: 0.2"
          />
        </div>

        {error && <div style={{ color: '#dc2626', marginTop: '0.5rem' }}>{error}</div>}

        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'יוצר...' : 'צור מוצר'}</button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

export default ProductsList;
